import { useDialog } from 'muibox';
import { useContext, useState } from 'react';
import { useQuery } from 'react-query';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';
import useSubscriber from './useSubscriber';

export namespace ElevenLabs {
    export type SpeakerSeparationResponseModelStatus = 'not_started' | 'pending' | 'completed' | 'failed';

    export interface UtteranceResponseModel {
        /** The start time of the utterance in seconds. */
        start: number;
        /** The end time of the utterance in seconds. */
        end: number;
    }

    export interface SpeakerResponseModel {
        /** The ID of the speaker. */
        speakerId: string;
        /** The duration of the speaker segment in seconds. */
        durationSecs: number;
        /** The utterances of the speaker. */
        utterances?: UtteranceResponseModel[];
    }

    export interface SpeakerSeparationResponseModel {
        /** The ID of the voice. */
        voiceId: string;
        /** The ID of the sample. */
        sampleId: string;
        /** The status of the speaker separation. */
        status: SpeakerSeparationResponseModelStatus;
        /** The speakers of the sample. */
        speakers?: Record<string, SpeakerResponseModel | undefined>;
        /** The IDs of the selected speakers. */
        selectedSpeakerIds?: string[];
    }

    export interface VoiceSample {
        /** The ID of the sample. */
        sampleId?: string;
        /** The name of the sample file. */
        fileName?: string;
        /** The MIME type of the sample file. */
        mimeType?: string;
        /** The size of the sample file in bytes. */
        sizeBytes?: number;
        /** The hash of the sample file. */
        hash?: string;
        durationSecs?: number;
        removeBackgroundNoise?: boolean;
        hasIsolatedAudio?: boolean;
        hasIsolatedAudioPreview?: boolean;
        speakerSeparation?: SpeakerSeparationResponseModel;
        trimStart?: number;
        trimEnd?: number;
    }

    /**
     * The category of the voice.
     */
    export type VoiceResponseModelCategory = 'generated' | 'cloned' | 'premade' | 'professional' | 'famous' | 'high_quality';

    export type FineTuningResponseModelStateValue = 'not_started' | 'queued' | 'fine_tuning' | 'fine_tuned' | 'failed' | 'delayed';

    export interface RecordingResponse {
        /** The ID of the recording. */
        recordingId: string;
        /** The MIME type of the recording. */
        mimeType: string;
        /** The size of the recording in bytes. */
        sizeBytes: number;
        /** The date of the recording in Unix time. */
        uploadDateUnix: number;
        /** The transcription of the recording. */
        transcription: string;
    }

    export interface VerificationAttemptResponse {
        /** The text of the verification attempt. */
        text: string;
        /** The date of the verification attempt in Unix time. */
        dateUnix: number;
        /** Whether the verification attempt was accepted. */
        accepted: boolean;
        /** The similarity of the verification attempt. */
        similarity: number;
        /** The Levenshtein distance of the verification attempt. */
        levenshteinDistance: number;
        /** The recording of the verification attempt. */
        recording?: RecordingResponse;
    }

    export interface ManualVerificationFileResponse {
        /** The ID of the file. */
        fileId: string;
        /** The name of the file. */
        fileName: string;
        /** The MIME type of the file. */
        mimeType: string;
        /** The size of the file in bytes. */
        sizeBytes: number;
        /** The date of the file in Unix time. */
        uploadDateUnix: number;
    }

    export interface ManualVerificationResponse {
        /** The extra text of the manual verification. */
        extraText: string;
        /** The date of the manual verification in Unix time. */
        requestTimeUnix: number;
        /** The files of the manual verification. */
        files: ManualVerificationFileResponse[];
    }

    export interface FineTuningResponse {
        /** Whether the user is allowed to fine-tune the voice. */
        isAllowedToFineTune?: boolean;
        /** The state of the fine-tuning process for each model. */
        state?: Record<string, FineTuningResponseModelStateValue>;
        /** List of verification failures in the fine-tuning process. */
        verificationFailures?: string[];
        /** The number of verification attempts in the fine-tuning process. */
        verificationAttemptsCount?: number;
        /** Whether a manual verification was requested for the fine-tuning process. */
        manualVerificationRequested?: boolean;
        /** The language of the fine-tuning process. */
        language?: string;
        /** The progress of the fine-tuning process. */
        progress?: Record<string, number | undefined>;
        /** The message of the fine-tuning process. */
        message?: Record<string, string | undefined>;
        /** The duration of the dataset in seconds. */
        datasetDurationSeconds?: number;
        /** The number of verification attempts. */
        verificationAttempts?: VerificationAttemptResponse[];
        /** List of slice IDs. */
        sliceIds?: string[];
        /** The manual verification of the fine-tuning process. */
        manualVerification?: ManualVerificationResponse;
        /** The maximum number of verification attempts. */
        maxVerificationAttempts?: number;
        /** The next maximum verification attempts reset time in Unix milliseconds. */
        nextMaxVerificationAttemptsResetUnixMs?: number;
        finetuningState?: unknown;
    }

    export interface VoiceSettings {
        /** Determines how stable the voice is and the randomness between each generation. Lower values introduce broader emotional range for the voice. Higher values can result in a monotonous voice with limited emotion. */
        stability?: number;
        /** This setting boosts the similarity to the original speaker. Using this setting requires a slightly higher computational load, which in turn increases latency. */
        useSpeakerBoost?: boolean;
        /** Determines how closely the AI should adhere to the original voice when attempting to replicate it. */
        similarityBoost?: number;
        /** Determines the style exaggeration of the voice. This setting attempts to amplify the style of the original speaker. It does consume additional computational resources and might increase latency if set to anything other than 0. */
        style?: number;
        /** Adjusts the speed of the voice. A value of 1.0 is the default speed, while values less than 1.0 slow down the speech, and values greater than 1.0 speed it up. */
        speed?: number;
    }

    export type VoiceSharingState = 'enabled' | 'disabled' | 'copied' | 'copied_disabled';

    export type VoiceSharingResponseModelCategory = 'generated' | 'cloned' | 'premade' | 'professional' | 'famous' | 'high_quality';

    export type ReviewStatus = 'not_requested' | 'pending' | 'declined' | 'allowed' | 'allowed_with_changes';

    export interface VoiceSharingModerationCheckResponseModel {
        /** The date the moderation check was made in Unix time. */
        dateCheckedUnix?: number;
        /** The name value of the voice. */
        nameValue?: string;
        /** Whether the name check was successful. */
        nameCheck?: boolean;
        /** The description value of the voice. */
        descriptionValue?: string;
        /** Whether the description check was successful. */
        descriptionCheck?: boolean;
        /** A list of sample IDs. */
        sampleIds?: string[];
        /** A list of sample checks. */
        sampleChecks?: number[];
        /** A list of captcha IDs. */
        captchaIds?: string[];
        /** A list of CAPTCHA check values. */
        captchaChecks?: number[];
    }

    export type ReaderResourceResponseModelResourceType = 'read' | 'collection';

    export interface ReaderResourceResponseModel {
        /** The type of resource. */
        resourceType: ReaderResourceResponseModelResourceType;
        /** The ID of the resource. */
        resourceId: string;
    }

    export interface VoiceSharingResponse {
        /** The status of the voice sharing. */
        status?: VoiceSharingState;
        /** The sample ID of the history item. */
        historyItemSampleId?: string;
        /** The date of the voice sharing in Unix time. */
        dateUnix?: number;
        /** A list of whitelisted emails. */
        whitelistedEmails?: string[];
        /** The ID of the public owner. */
        publicOwnerId?: string;
        /** The ID of the original voice. */
        originalVoiceId?: string;
        /** Whether financial rewards are enabled. */
        financialRewardsEnabled?: boolean;
        /** Whether free users are allowed. */
        freeUsersAllowed?: boolean;
        /** Whether live moderation is enabled. */
        liveModerationEnabled?: boolean;
        /** The rate of the voice sharing. */
        rate?: number;
        /** The rate of the voice sharing in USD per 1000 credits. */
        fiatRate?: number;
        /** The notice period of the voice sharing. */
        noticePeriod?: number;
        /** The date of the voice sharing in Unix time. */
        disableAtUnix?: number;
        /** Whether voice mixing is allowed. */
        voiceMixingAllowed?: boolean;
        /** Whether the voice is featured. */
        featured?: boolean;
        /** The category of the voice. */
        category?: VoiceSharingResponseModelCategory;
        /** Whether the reader app is enabled. */
        readerAppEnabled?: boolean;
        /** The image URL of the voice. */
        imageUrl?: string;
        /** The ban reason of the voice. */
        banReason?: string;
        /** The number of likes on the voice. */
        likedByCount?: number;
        /** The number of clones on the voice. */
        clonedByCount?: number;
        /** The name of the voice. */
        name?: string;
        /** The description of the voice. */
        description?: string;
        /** The labels of the voice. */
        labels?: Record<string, string>;
        /** The review status of the voice. */
        reviewStatus?: ReviewStatus;
        /** The review message of the voice. */
        reviewMessage?: string;
        /** Whether the voice is enabled in the library. */
        enabledInLibrary?: boolean;
        /** The Instagram username of the voice. */
        instagramUsername?: string;
        /** The Twitter/X username of the voice. */
        twitterUsername?: string;
        /** The YouTube username of the voice. */
        youtubeUsername?: string;
        /** The TikTok username of the voice. */
        tiktokUsername?: string;
        /** The moderation check of the voice. */
        moderationCheck?: VoiceSharingModerationCheckResponseModel;
        /** The reader restricted on of the voice. */
        readerRestrictedOn?: ReaderResourceResponseModel[];
    }

    export interface VerifiedVoiceLanguageResponseModel {
        /** The language of the voice. */
        language: string;
        /** The voice's model ID. */
        modelId: string;
        /** The voice's accent, if applicable. */
        accent?: string;
        /** The voice's locale, if applicable. */
        locale?: string;
        /** The voice's preview URL, if applicable. */
        previewUrl?: string;
    }

    export type VoiceResponseModelSafetyControl =
        | 'NONE'
        | 'BAN'
        | 'CAPTCHA'
        | 'CAPTCHA_AND_MODERATION'
        | 'ENTERPRISE_BAN'
        | 'ENTERPRISE_CAPTCHA';

    export interface RecordingResponse {
        /** The ID of the recording. */
        recordingId: string;
        /** The MIME type of the recording. */
        mimeType: string;
        /** The size of the recording in bytes. */
        sizeBytes: number;
        /** The date of the recording in Unix time. */
        uploadDateUnix: number;
        /** The transcription of the recording. */
        transcription: string;
    }
    export interface VerificationAttemptResponse {
        /** The text of the verification attempt. */
        text: string;
        /** The date of the verification attempt in Unix time. */
        dateUnix: number;
        /** Whether the verification attempt was accepted. */
        accepted: boolean;
        /** The similarity of the verification attempt. */
        similarity: number;
        /** The Levenshtein distance of the verification attempt. */
        levenshteinDistance: number;
        /** The recording of the verification attempt. */
        recording?: RecordingResponse;
    }

    export interface VoiceVerificationResponse {
        /** Whether the voice requires verification. */
        requiresVerification: boolean;
        /** Whether the voice has been verified. */
        isVerified: boolean;
        /** List of verification failures. */
        verificationFailures: string[];
        /** The number of verification attempts. */
        verificationAttemptsCount: number;
        /** The language of the voice. */
        language?: string;
        /** Number of times a verification was attempted. */
        verificationAttempts?: VerificationAttemptResponse[];
    }

    export interface Voice {
        /** The ID of the voice. */
        voiceId: string;
        /** The name of the voice. */
        name?: string;
        /** List of samples associated with the voice. */
        samples?: VoiceSample[];
        /** The category of the voice. */
        category?: VoiceResponseModelCategory;
        /** Fine-tuning information for the voice. */
        fineTuning?: FineTuningResponse;
        /** Labels associated with the voice. */
        labels?: Record<string, string>;
        /** The description of the voice. */
        description?: string;
        /** The preview URL of the voice. */
        previewUrl?: string;
        /** The tiers the voice is available for. */
        availableForTiers?: string[];
        /** The settings of the voice. */
        settings?: VoiceSettings;
        /** The sharing information of the voice. */
        sharing?: VoiceSharingResponse;
        /** The base model IDs for high-quality voices. */
        highQualityBaseModelIds?: string[];
        /** The verified languages of the voice. */
        verifiedLanguages?: VerifiedVoiceLanguageResponseModel[];
        /** The safety controls of the voice. */
        safetyControl?: VoiceResponseModelSafetyControl;
        /** The voice verification of the voice. */
        voiceVerification?: VoiceVerificationResponse;
        /** The permission on the resource of the voice. */
        permissionOnResource?: string;
        /** Whether the voice is owned by the user. */
        isOwner?: boolean;
        /** Whether the voice is legacy. */
        isLegacy?: boolean;
        /** Whether the voice is mixed. */
        isMixed?: boolean;
        /** The creation time of the voice in Unix time. */
        createdAtUnix?: number;
    }
}

const useTtsElevenlabsVoices = (enabled: boolean) => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const { data: subscriber } = useSubscriber();
    const dialog = useDialog();
    const axios = useAxios();

    const [ttsGenerateTestLoading, setTtsGenerateTestLoading] = useState<boolean>(false);

    // Fetch Subscriber's Mass DM
    const fetchData = async (): Promise<ElevenLabs.Voice[] | null> => {
        const query: string = `${settingsContext.routes.tts.base}${subscriber._id}/elevenLabs/voices`;

        return axios
            .get(query)
            .then(response => response.data)
            .catch(() => {
                return null;
            });
    };

    const elevenLabsVoices = useQuery(['elevenLabsVoices', subscriber?._id], () => fetchData(), {
        refetchOnWindowFocus: false,
        staleTime: Infinity,
        enabled: userContext.jwtToken && settingsContext.apiKey && subscriber && subscriber._id && enabled ? true : false,
    });

    const ttsGenerateTest = async (
        modelId: string,
        prompt: string,
    ): Promise<{ success: boolean; message: string; url: string; fileName: string } | null> => {
        setTtsGenerateTestLoading(true);

        const query: string = `${settingsContext.routes.tts.base}${subscriber._id}/referenceModel/generateTestTts/${modelId}`;
        return axios
            .post(query, { prompt })
            .then(response => {
                setTtsGenerateTestLoading(false);

                if (response.status !== 200) {
                    console.error('Error generating TTS test:', response.data);

                    return null;
                }

                return response.data;
            })
            .catch(error => {
                setTtsGenerateTestLoading(false);

                console.error('Error generating TTS test:', error);

                handleHttpError(error, dialog);

                return null;
            });
    };

    return { ...elevenLabsVoices, ttsGenerateTest, ttsGenerateTestLoading };
};

export default useTtsElevenlabsVoices;
